// root '../../../';

// dependencies
import { Child } from 'pym.js';
import { select } from 'd3-selection';

// polyfills
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import config from './config';
import graphic from './graphic';

// styles
import './styles/index.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

const DATA_URL = 'https://ix.cnn.io/data/earthquakes-next/archive/index.json';

let cachedData = {};

function draw(initialWidth) {
  const container = select('#graphic');
  container.html('');

  const fallbackWidth = container.node().clientWidth || 600;
  const safeWidth = initialWidth !== fallbackWidth ? fallbackWidth : initialWidth;
  const isMobile = safeWidth <= 500;

  const graphicConfig = config({ initialWidth: safeWidth, isMobile }, cachedData);
  graphic(graphicConfig, container, isMobile);

  const { pymChild } = window;
  if (pymChild) pymChild.sendHeight();
}

async function fetchData() {
  const res = await fetch(DATA_URL);
  if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
  return res.json();
}

// Schedules a callback at the next 10am ET (EDT = UTC-4, EST = UTC-5),
// then re-schedules itself each day.
function scheduleNextRefresh(callback) {
  const now = new Date();
  // EDT: approximately March–October; EST: November–February
  const isEDT = now.getUTCMonth() >= 2 && now.getUTCMonth() <= 9;
  const etOffsetMs = (isEDT ? -4 : -5) * 3600000;

  // Shift "now" into ET space so we can do simple date arithmetic
  const etNow = new Date(now.getTime() + etOffsetMs);
  const next10am = new Date(etNow);
  next10am.setUTCHours(10, 0, 0, 0);
  if (next10am <= etNow) next10am.setUTCDate(next10am.getUTCDate() + 1);

  const delay = next10am - etNow;
  setTimeout(async () => {
    await callback();
    scheduleNextRefresh(callback);
  }, delay);
}

function updateCopy() {
  const currentYear = String(cachedData.endYear || new Date().getFullYear());
  const currentYearData = (cachedData.years || [])
    .find(d => String(d.year) === currentYear);
  const currentYearCount = currentYearData
    ? Number(currentYearData.mag6Count).toLocaleString('en-US')
    : '—';

  select('#meta .subhed').html(
    `There have been ${currentYearCount} quakes with a magnitude of 6 or higher in ${currentYear}.`,
  );

  const footnote = cachedData.lastUpdatedStr
    ? `Note: Data last updated ${cachedData.lastUpdatedStr}. Counts only include quakes with a magnitude of 6 or higher, not all those tagged as significant by the US Geological Survey. Significant quakes weren't tracked as closely in previous decades.`
    : '';
  select('#footer .footnote').text(footnote);
  select('#footer .source').text(`Source: ${cachedData.source || 'US Geological Survey'}`);
}

async function refresh() {
  try {
    cachedData = await fetchData();
    const w = select('#graphic').node().clientWidth || 600;
    draw(w);
    updateCopy();
    if (window.pymChild) window.pymChild.sendHeight();
  } catch (e) {
    console.error('Failed to refresh earthquake data:', e);
  }
}

async function init() {
  try {
    cachedData = await fetchData();
  } catch (e) {
    console.error('Failed to fetch earthquake data:', e);
  }

  // Set footer before pym init so first height measurement includes source/credit
  updateCopy();

  window.pymChild = new Child({ renderCallback: draw, id: pkg.name });

  scheduleNextRefresh(refresh);

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);
      if (node) node.innerHTML = meta[id];
    });
  }

  // Re-apply after meta injection overrides, then nudge pym to re-measure
  updateCopy();
  if (window.pymChild) window.pymChild.sendHeight();
}

init();
